<template>
  <div class="setting-drawer">
    <a-drawer width="300"
              placement="right"
              :closable="false"
              @close="onClose"
              :visible="visible"
              style="height: 100%;overflow: auto;">
      <div class="setting-drawer-index-content">
        <div :style="{ marginBottom: '24px' }" v-if="langList.length">
          <h3 class="setting-drawer-index-title">{{ $t('systemBase.languageSwitching') }}</h3>
          <a-form :form="form" :label-col="{ span: 5 }" :wrapper-col="{ span: 12 }">
            <a-form-item :label="$t('systemBase.mainLanguage')">
              <a-select :value="defaultLocal" style="width: 200px" @change="changeLang">
                <a-select-option :value="item.languageCode" v-for="(item, index) in langList" :key="index">
                  {{ item.languageName }}
                </a-select-option>
              </a-select>
            </a-form-item>
            <a-form-item :label="$t('systemBase.subLanguage')">
              <a-select :value="secondLanguage" mode="multiple" style="width: 200px" @change="changeSecondLang">
                <a-select-option :disabled="item.disabled" :value="item.languageCode" v-for="(item, index) in langList" :key="index">
                  {{ item.languageName }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-form>
          <div class="change-locale"></div>
        </div>
        <a-divider v-if="langList.length" />
        <div :style="{ marginBottom: '24px' }">
          <h3 class="setting-drawer-index-title">{{ $t('systemBase.overallStyleSetting') }}</h3>
          <div class="setting-drawer-index-blockChecbox">
            <a-tooltip>
              <template slot="title">
                {{ $t('systemBase.darkMenuStyle') }}
              </template>
              <div class="setting-drawer-index-item" @click="handleMenuTheme('dark')">
                <img src="https://gw.alipayobjects.com/zos/rmsportal/LCkqqYNmvBEbokSDscrm.svg" alt="dark">
                <div class="setting-drawer-index-selectIcon" v-if="navTheme === 'dark'">
                  <a-icon type="check" />
                </div>
              </div>
            </a-tooltip>
            <a-tooltip>
              <template slot="title">
                {{ $t('systemBase.brightMenuStyle') }}
              </template>
              <div class="setting-drawer-index-item" @click="handleMenuTheme('light')">
                <img src="https://gw.alipayobjects.com/zos/rmsportal/jpRkZQMyYRryryPNtyIC.svg" alt="light">
                <div class="setting-drawer-index-selectIcon" v-if="navTheme !== 'dark'">
                  <a-icon type="check" />
                </div>
              </div>
            </a-tooltip>
          </div>
        </div>
        <div :style="{ marginBottom: '24px' }">
          <h3 class="setting-drawer-index-title">{{ $t('systemBase.themeColor') }}</h3>
          <div style="height: 20px">
            <a-tooltip class="setting-drawer-theme-color-colorBlock" v-for="(item, index) in colorList" :key="index">
              <template slot="title">
                {{ item.key }}
              </template>
              <a-tag :color="item.color" @click="changeColor(item.color)">
                <a-icon type="check" v-if="item.color === primaryColor"></a-icon>
              </a-tag>
            </a-tooltip>
          </div>
        </div>
        <a-divider />
        <div :style="{ marginBottom: '24px' }">
          <h3 class="setting-drawer-index-title">{{ $t('systemBase.navigationMode') }}</h3>
          <div class="setting-drawer-index-blockChecbox">
            <a-tooltip>
              <template slot="title">
                {{ $t('systemBase.sidebarNavigation') }}
              </template>
              <div class="setting-drawer-index-item" @click="handleLayout('sidemenu')">
                <img src="https://gw.alipayobjects.com/zos/rmsportal/JopDzEhOqwOjeNTXkoje.svg" alt="sidemenu">
                <div class="setting-drawer-index-selectIcon" v-if="layoutMode === 'sidemenu'">
                  <a-icon type="check" />
                </div>
              </div>
            </a-tooltip>
            <a-tooltip>
              <template slot="title">
                {{ $t('systemBase.topBarNavigation') }}
              </template>
              <div class="setting-drawer-index-item" @click="handleLayout('topmenu')">
                <img src="https://gw.alipayobjects.com/zos/rmsportal/KDNDBbriJhLwuqMoxcAr.svg" alt="topmenu">
                <div class="setting-drawer-index-selectIcon" v-if="layoutMode !== 'sidemenu'">
                  <a-icon type="check" />
                </div>
              </div>
            </a-tooltip>
          </div>
          <div :style="{ marginTop: '24px' }">
            <a-list :split="false">
              <a-list-item>
                <a-tooltip slot="actions">
                  <template slot="title">
                    {{ $t('systemBase.topBarNavigationTip') }}
                  </template>
                  <a-select size="small" style="width: 80px;" :defaultValue="contentWidth" @change="handleContentWidthChange">
                    <a-select-option value="Fixed">{{ $t('systemBase.fixed') }}</a-select-option>
                    <a-select-option value="Fluid" v-if="layoutMode !== 'sidemenu'">{{ $t('systemBase.flowType') }}</a-select-option>
                  </a-select>
                </a-tooltip>
                <a-list-item-meta>
                  <div slot="title">{{ $t('systemBase.contentAreaWidth') }}</div>
                </a-list-item-meta>
              </a-list-item>
              <a-list-item>
                <a-switch slot="actions" size="small" :defaultChecked="language" @change="handleLanguage" @click="changeIsLang" />
                <a-list-item-meta>
                  <div slot="title">{{ $t('systemBase.multiLanguage') }}</div>
                </a-list-item-meta>
              </a-list-item>
              <a-list-item>
                <a-switch slot="actions" size="small" :defaultChecked="fixedHeader" @change="handleFixedHeader" />
                <a-list-item-meta>
                  <div slot="title">{{ $t('systemBase.fixedHeader') }}</div>
                </a-list-item-meta>
              </a-list-item>
              <a-list-item>
                <a-switch slot="actions" size="small" :disabled="!fixedHeader" :defaultChecked="autoHideHeader" @change="handleFixedHeaderHidden" />
                <a-list-item-meta>
                  <div slot="title" :style="{ textDecoration: !fixedHeader ? 'line-through' : 'unset' }">{{ $t('systemBase.hideHeaderWhenSliding') }}</div>
                </a-list-item-meta>
              </a-list-item>
              <a-list-item>
                <a-switch slot="actions" size="small" :disabled="(layoutMode === 'topmenu')" :checked="dataFixSiderbar" @change="handleFixSiderbar" />
                <a-list-item-meta>
                  <div slot="title" :style="{ textDecoration: layoutMode === 'topmenu' ? 'line-through' : 'unset' }">{{ $t('systemBase.fixedSideMenu') }}</div>
                </a-list-item-meta>
              </a-list-item>
            </a-list>
          </div>
        </div>
        <a-divider />
        <div :style="{ marginBottom: '24px' }">
          <h3 class="setting-drawer-index-title">{{ $t('systemBase.otherSettings') }}</h3>
          <div>
            <a-list :split="false">
              <a-list-item>
                <a-switch slot="actions" size="small" :defaultChecked="colorWeak" @change="onColorWeak" />
                <a-list-item-meta>
                  <div slot="title">{{ $t('systemBase.colorWeakMode') }}</div>
                </a-list-item-meta>
              </a-list-item>
              <a-list-item>
                <a-switch slot="actions" size="small" :defaultChecked="multipage" @change="onMultipageWeak" />
                <a-list-item-meta>
                  <div slot="title">{{ $t('systemBase.multiTabMode') }}</div>
                </a-list-item-meta>
              </a-list-item>
            </a-list>
          </div>
        </div>
        <a-divider />
        <div :style="{ marginBottom: '24px' }">
          <a-alert type="warning">
            <span slot="message">
              {{ $t('systemBase.settingTips') }}
              <a href="https://github.com/sendya/ant-design-pro-vue/blob/master/src/defaultSettings.js" target="_blank">src/defaultSettings.js</a>
            </span>
          </a-alert>
        </div>
      </div>
      <div class="setting-drawer-index-handle" @click="toggle" v-if="visible">
        <!--        <a-icon type="setting" v-if="!visible"/>-->
        <!--        <a-icon type="close" v-else/>-->
        <a-icon type="close" />
      </div>
    </a-drawer>
  </div>
</template>

<script>
import DetailList from '@/components/tools/DetailList'
import SettingItem from '@/components/setting/SettingItem'
import config from '@/defaultSettings'
import { updateTheme, updateColorWeak, colorList } from '@/components/tools/setting'
import { mixin, mixinDevice } from '@/utils/mixin.js'
import { getAction, putAction } from '@/api/manage'
import { triggerWindowResizeEvent } from '@/utils/util'
import { mainLanguageAndSecondLanguage, setLanguage, sysLanguageConfiguration } from '@api/api'
import Vue from 'vue'
import { DEFAULT_LANGUAGE } from '@/store/mutation-types'

export default {
  components: {
    DetailList,
    SettingItem
  },
  mixins: [mixin, mixinDevice],
  data() {
    return {
      form: {},
      defaultLocal: '',
      secondLanguage: [],
      langList: [],
      visible: false,
      colorList,
      dataFixSiderbar: false
    }
  },
  mounted() {
    // 当主题色不是默认色时，才进行主题编译
    if (this.primaryColor !== config.primaryColor) {
      updateTheme(this.primaryColor)
    }
    if (this.colorWeak !== config.colorWeak) {
      updateColorWeak(this.colorWeak)
    }
    if (this.multipage !== config.multipage) {
      this.$store.dispatch('ToggleMultipage', this.multipage)
    }
    let isLang = this.$ls.get(DEFAULT_LANGUAGE) || false
    this.handleLanguage(isLang)
    this.getLangLists()
  },
  methods: {
    getLangLists() {
      sysLanguageConfiguration().then(res => {
        // validFlag
        let langList = res.result || []
        this.langList = []
        langList.map(item => {
          if (item.validFlag == 'Y') {
            this.langList.push(item)
          }
        })
        this.$ls.set('languageList', JSON.stringify(this.langList))
        this.getLang()
      })
    },
    getLang() {
      mainLanguageAndSecondLanguage().then(res => {
        this.defaultLocal = res.result.mainLanguage || this.$ls.get('language', 'zh-cn')
        this.langList.map(item => {
          if (item.languageCode == this.defaultLocal) {
            item.disabled = true
          } else {
            item.disabled = false
          }
        })
        this.secondLanguage = res.result.secondLanguage || JSON.parse(this.$ls.get('secondLanguage')) || []
        this.secondLanguage.length && this.secondLanguage.map((item, index) => {
          if (item == this.defaultLocal) {
            this.secondLanguage.splice(index, 1)
          }
        })
        this.$ls.set('secondLanguage', JSON.stringify(this.secondLanguage))
      })
    },
    changeLang(e) {
      this.langList.map(item => {
        if (item.languageCode == e) {
          item.disabled = true
        } else {
          item.disabled = false
        }
      })
      let _request = {
        mainLanguage: e
      }
      setLanguage(_request).then(res => {
        this.$message.success(res.message)
        this.$bus.$emit('changeLang', e)
        this.$bus.$emit('changeLangGetList')
        this.defaultLocal = e
        let flag = false
        this.secondLanguage.length && this.secondLanguage.map((item, index) => {
          if (item == this.defaultLocal) {
            this.secondLanguage.splice(index, 1)
            flag = true
          }
        })
        flag && this.changeSecondLang(this.secondLanguage)
        this.toggle()
        window.location.reload()
      })
    },
    changeSecondLang(e) {
      this.secondLanguage = e
      let _request = {
        secondLanguage: e
      }
      setLanguage(_request).then(res => {
        this.$message.success(res.message)
        this.$ls.set('secondLanguage', JSON.stringify(e))
        this.$bus.$emit('changeLangGetList')
        // window.location.reload()
      })
    },
    showDrawer() {
      this.visible = true
    },
    onClose() {
      this.visible = false
    },
    toggle() {
      this.visible = !this.visible
    },
    onColorWeak(checked) {
      this.$store.dispatch('ToggleWeak', checked)
      updateColorWeak(checked)
    },
    onMultipageWeak(checked) {
      this.$store.dispatch('ToggleMultipage', checked)
    },
    handleMenuTheme(theme) {
      this.$store.dispatch('ToggleTheme', theme)
    },
    handleLayout(mode) {
      this.$store.dispatch('ToggleLayoutMode', mode)
      // 因为顶部菜单不能固定左侧菜单栏，所以强制关闭
      this.handleFixSiderbar(false)
      // 触发窗口resize事件
      triggerWindowResizeEvent()
    },
    handleContentWidthChange(type) {
      this.$store.dispatch('ToggleContentWidth', type)
    },
    changeColor(color) {
      if (this.primaryColor !== color) {
        this.$store.dispatch('ToggleColor', color)
        updateTheme(color)
      }
    },
    handleLanguage(language) {
      this.$store.commit('TOGGLE_LANGUAGE', language)
    },
    changeIsLang() {
      window.location.reload()
    },
    handleFixedHeader(fixed) {
      this.$store.dispatch('ToggleFixedHeader', fixed)
    },
    handleFixedHeaderHidden(autoHidden) {
      this.$store.dispatch('ToggleFixedHeaderHidden', autoHidden)
    },
    handleFixSiderbar(fixed) {
      if (this.layoutMode === 'topmenu') {
        fixed = false
      }
      this.dataFixSiderbar = fixed
      this.$store.dispatch('ToggleFixSiderbar', fixed)
    }
  }
}
</script>

<style lang="less" scoped>

.setting-drawer-index-content {

  .setting-drawer-index-blockChecbox {
    display: flex;

    .setting-drawer-index-item {
      margin-right: 16px;
      position: relative;
      border-radius: 4px;
      cursor: pointer;

      img {
        width: 48px;
      }

      .setting-drawer-index-selectIcon {
        position: absolute;
        top: 0;
        right: 0;
        width: 100%;
        padding-top: 15px;
        padding-left: 24px;
        height: 100%;
        color: #1890ff;
        font-size: 14px;
        font-weight: 700;
      }
    }
  }

  .setting-drawer-theme-color-colorBlock {
    width: 20px;
    height: 20px;
    border-radius: 2px;
    float: left;
    cursor: pointer;
    margin-right: 8px;
    padding-left: 0px;
    padding-right: 0px;
    text-align: center;
    color: #fff;
    font-weight: 700;

    i {
      font-size: 14px;
    }
  }
}

.setting-drawer-index-handle {
  position: absolute;
  top: 240px;
  background: #1890ff;
  width: 48px;
  height: 48px;
  right: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  pointer-events: auto;
  z-index: 1001;
  text-align: center;
  font-size: 16px;
  border-radius: 4px 0 0 4px;

  i {
    color: rgb(255, 255, 255);
    font-size: 20px;
  }
}
</style>
